import { FolderClosed, Globe, Images, Loader2 } from "lucide-react"
import { ChatInput } from "features/chat/components/ChatInput"
import { useChatStore } from "state/store/chat/use-chat-store"
import { Attachment } from "/features/chat/types/chat";
import { useNavigate, useParams } from "react-router-dom";
import { ChatInputButton } from "../chat/chat";
import { useEffect, useMemo, useState } from "react";
import { ButtonIds, initialFeaturesState } from "features/chat/types/chat";
import { useConfig } from "hooks/use-config";
import { ButtonTypes, Alignment } from "pages/chat/chat";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "hooks/use-mobile";
import { useFetchProject, useFetchProjectConversations } from "features/projects/hooks/use-fetch-project";
import { ConversationActions } from "features/projects/components/conversation-actions";
import { useToast } from "../../hooks/use-toast";
import { CustomtDateFormat } from "lib/custom-date-formatter";
import { trackEvent } from "analytics/tracker";
import { EVENTS, InitialPromptType } from "analytics/events";

// import AddFilesModal from "features/projects/components/add-files-modal";
// import ProjectActionsDropdown from "features/projects/components/project-actions-dropdown";
// import AddInstructionModal from "features/projects/components/add-instruction-modal";
// import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";

import { useInviteAssistant } from "features/group-chat/hooks/use-invite";
import { InvitationPayload } from "features/group-chat/services/group-services";
import { useModelStore } from "state/store/chat/use-model-store";
import { useFetchProjectFiles } from "features/projects/hooks/use-projects";
import AddFilesModal from "features/projects/components/add-files-modal";
import AddInstructionModal from "features/projects/components/add-instruction-modal";
import ProjectActionsDropdown from "features/projects/components/project-actions-dropdown";

export default function ProjectPage() {

    const sendMessageToProject = useChatStore((state) => state.sendMessageToProject);
    const setActiveConversationId = useChatStore((state) => state.setActiveConversationId);
    // const sendGroupChatStream = useChatStore((state) => state.sendGroupChatStream);
    // const temporaryConversation = useChatStore((state) => state.temporaryConversation);
    // const replaceConversationId = useChatStore((state) => state.replaceConversationId);
    // const sendMessageToProject = useChatStore((state) => state.sendMessageToProject);
    // const replyMessage = useChatStore((state) => state.replyMessage);
    const navigate = useNavigate();
    const { toast } = useToast();
    const { projectId } = useParams();
    // const [inputButtons, setInputButtons] = useState<ChatInputButton[]>([]);
    const [openFilesModal, setOpenFilesModal] = useState(false);
    const [isAddInstructionModalOpen, setIsAddInstructionModalOpen] = useState(false)

    const [isFeatureSelected, setIsFeatureSelected] =
        useState<Record<ButtonIds, boolean>>(initialFeaturesState);
    const { configurationSettings } = useConfig();
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [hoveredConversations, setHoveredConversations] = useState<Record<string, boolean>>({});
    const inviteAssistantMutation = useInviteAssistant();
    const selectedModel = useModelStore.getState().selectedModel;

    const sidebarProjectsList = useChatStore((state) => state.sidebarProjectsList);
    const updateProjectItems = useChatStore((state) => state.updateProjectItems);
    const setSidebarProjectsList = useChatStore((state) => state.setSidebarProjectsList);
    const projectFromStore = sidebarProjectsList.find((project) => project.id === projectId);

    const { data: fetchedProject, refetch: refetchProject, isError: isFetchingProjectMetadataError, isFetching: isFetchingProject } = useFetchProject(
        projectId ?? '',
        {
            enabled: !projectFromStore,
        }
    );


    const { data: fetchedProjectConversations, isLoading: isFetchingProjectConversations, isFetchedAfterMount } = useFetchProjectConversations(
        projectId ?? '',
        {
            enabled: projectFromStore && !projectFromStore.items?.length,
        }
    );

    console.log('fetchedProject', fetchedProject)

    const [selectedSources, setSelectedSources] = useState<string[]>([]);
    const [isSourcesToolActive, setIsSourcesToolActive] = useState<boolean>(false);

    useEffect(() => {
        if (!fetchedProject) return;

        const exists = sidebarProjectsList.some(p => p.id === fetchedProject.id);
        if (!exists) {
            const updatedProjects = [...sidebarProjectsList];
            updatedProjects.push(fetchedProject);
            setSidebarProjectsList(updatedProjects);
        }
    }, [fetchedProject?.id, sidebarProjectsList, setSidebarProjectsList]);

    useEffect(() => {
        if (!isFetchedAfterMount || !fetchedProjectConversations || !projectId) return;
        // console.log('fetchedProjectConversations', fetchedProjectConversations)
        updateProjectItems(projectId, fetchedProjectConversations);
    }, [fetchedProjectConversations, updateProjectItems])


    useEffect(() => {
        if (!isDropdownOpen) {
            setHoveredConversations({});
        }
    }, [isDropdownOpen]);

    const inputButtons = useMemo<ChatInputButton[]>(() => [
        {
            id: ButtonIds.SEARCH,
            label: t('SEARCH'),
            icon: <Globe size={16} />,
            onClick: () => {
                setIsFeatureSelected((prev) => ({
                    ...initialFeaturesState,
                    [ButtonIds.SEARCH]: !prev[ButtonIds.SEARCH],
                }));
            },
            type: isMobile ? ButtonTypes.ICON : ButtonTypes.BUTTON,
            alignment: Alignment.LEFT,
            isDisabled: isFeatureSelected[ButtonIds.CREATE_IMAGE] || selectedSources.length > 0,
            isSelected: isFeatureSelected[ButtonIds.SEARCH],
        },
        {
            id: ButtonIds.CREATE_IMAGE,
            label: t('CREATE_IMAGE'),
            icon: <Images size={16} />,
            onClick: () => {
                setIsFeatureSelected((prev) => ({
                    ...initialFeaturesState,
                    [ButtonIds.CREATE_IMAGE]: !prev[ButtonIds.CREATE_IMAGE],
                }));
            },
            type: isMobile ? ButtonTypes.ICON : ButtonTypes.BUTTON,
            alignment: Alignment.LEFT,
            isDisabled: selectedSources.length > 0,
            isSelected: isFeatureSelected[ButtonIds.CREATE_IMAGE],
        }
    ], [isMobile, isFeatureSelected, t, selectedSources.length]);

    useEffect(() => {
        if (isFetchingProjectMetadataError) {
            toast({
                variant: "destructive",
                title: t('NOT_FOUND'),
                description: t('PROJECT_NOT_FOUND'),
            });
            navigate('/chat');
        }
    }, [isFetchingProjectMetadataError, toast, navigate]);


    const sendMessage = async (content: string, files: Attachment[]) => {
        setActiveConversationId(null);
        if (!projectId)
            return;

        // const { temporaryConversationId } = temporaryConversation(content, files, isFeatureSelected);
        // // console.log('temporaryConversationId in sendMessage', temporaryConversationId)
        // navigate(`/chat`, {
        //     state: {
        //         from: 'project',
        //         preservedFeatures: isFeatureSelected
        //     }
        // });

        const payload: InvitationPayload = {
            invitationType: 'ADD_USER',
            assistantId: selectedModel?.id,
        };
        const response = await inviteAssistantMutation.mutateAsync(payload);

        if (response?.conversationId) {
            const newConversationId = response?.conversationId;

            // Replace the temporary conversationId with the real one
            // replaceConversationId(temporaryConversationId, newConversationId);

            trackEvent(EVENTS.NEW_CHAT_STARTED(newConversationId, InitialPromptType.TEXT));

            // const mentions = [
            //     selectedModel?.id
            // ];
            await sendMessageToProject(content, configurationSettings, projectId, newConversationId, files, isFeatureSelected);
            // await sendGroupChatStream(content, configurationSettings, newConversationId, files as unknown as FileMetadata[], replyMessage, mentions as string[]);

            navigate(`/chat/${newConversationId}/${projectId}`, {
                state: {
                    from: 'project',
                    preservedFeatures: isFeatureSelected,
                    preservedSources: selectedSources,
                    preservedSourcesToolActive: isSourcesToolActive
                }
            });
        }
    };

    const { data: projectFiles } = useFetchProjectFiles({ projectId: projectId ?? '', page: 1, pageSize: 10 });

    console.log('projectFiles', projectFiles)



    // const sendMessage = async (content: string, files: Attachment[]) => {
    //     setActiveConversationId(null);
    //     if (!projectId)
    //         return;

    //     const payload: InvitationPayload = {
    //         invitationType: 'ADD_USER',
    //         assistantId: selectedModel?.id,
    //     };
    //     const response = await inviteAssistantMutation.mutateAsync(payload);

    //     if (response?.conversationId) {
    //         const newConversationId = response?.conversationId;
    //         trackEvent(EVENTS.NEW_CHAT_STARTED(newConversationId, InitialPromptType.TEXT));
    //         sendMessageToProject(
    //             content,
    //             configurationSettings,
    //             projectId,
    //             newConversationId,
    //             files,
    //             isFeatureSelected
    //         );
    //         navigate(`/chat/${newConversationId}`, {
    //             state: {
    //                 from: 'project',
    //                 preservedFeatures: isFeatureSelected
    //             }
    //         });
    //     }
    // };
    if (!projectId) return (<div className="absolute inset-0 z-10 flex items-start justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
    </div>)

    const project = projectFromStore || fetchedProject;
    // console.log('projectFiles', projectFiles)

    return (
        <div className="flex flex-col gap-2 w-full items-center justify-center pt-4 sm:pt-6 md:pt-8 pb-0">
            <div className="w-[95%] md:w-[80%] lg:w-[67%] flex items-center justify-between">
                <div className="flex items-center w-full gap-3 py-2 px-2">
                    <FolderClosed size={30} strokeWidth={2.2} />
                    <h1 className="text-2xl font-semibold">{project?.name}</h1>
                </div>

                <div className="flex items-center gap-3">
                    <div className="flex items-center justify-end w-full pr-2">
                        <ProjectActionsDropdown
                            fetchedProject={project}
                            refetchProject={refetchProject}
                            setIsAddInstructionModalOpen={setIsAddInstructionModalOpen}
                            setOpenFilesModal={setOpenFilesModal}
                            totalFiles={projectFiles?.totalCount ?? 0}
                        />
                    </div>
                </div>

            </div>
            <div className={`bottom-8 sm:bottom-6 md:bottom-8  flex flex-col gap-4 items-center justify-center shadow-[var(--tw-inset-shadow),var(--tw-inset-ring-shadow),var(--tw-ring-offset-shadow),var(--tw-ring-shadow),var(--tw-shadow)] w-[95%] md:w-[80%] lg:w-[67%]`}>
                <ChatInput
                    onSendMessage={sendMessage}
                    isLoading={false}
                    inputButtons={inputButtons}
                    isCurrentResponseGenerating={false}
                    selectedSources={selectedSources}
                    onSourcesChange={setSelectedSources}
                    isFeatureSelected={isFeatureSelected}
                    isSourcesToolActive={isSourcesToolActive}
                    onToggleTool={(toolId) => {
                        setIsFeatureSelected((prev) => ({
                            ...initialFeaturesState,
                            [toolId]: !prev[toolId],
                        }));
                    }}
                    onToggleSourcesTool={() => {
                        // Clear all other features when sources tool is toggled
                        setIsFeatureSelected(initialFeaturesState);
                        setIsSourcesToolActive(!isSourcesToolActive);
                    }}
                    isCancelOnGoing={false}
                />


                {
                    isFetchingProjectConversations &&
                    <div className="w-full flex flex-col gap-2 mt-4">
                        <div className="inset-0 flex items-center justify-center">
                            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                        </div>
                    </div>
                }

                <div className="w-full flex flex-col gap-2">
                    {project?.items?.map((item) => {
                        const lastConversation = item.Conversations?.[item.Conversations.length - 1];
                        const lastQuestion = lastConversation?.content || 'NO QUESTION';

                        return (
                            <div onClick={() => { navigate(`/chat/${item.ConversationId}/${projectId}`) }} key={item.ConversationId} className="w-full flex items-center justify-between gap-2 hover:bg-muted p-2 cursor-pointer" onMouseEnter={() =>
                                setHoveredConversations((prev) => ({
                                    ...prev,
                                    [item.ConversationId]: true,
                                }))
                            }
                                onMouseLeave={() =>
                                    !isDropdownOpen && setHoveredConversations((prev) => ({
                                        ...prev,
                                        [item.ConversationId]: false,
                                    }))
                                }>
                                <div className="flex flex-col max-w-[80%]">
                                    <span className="text-sm font-medium truncate">{item.ConversationTitle}</span>
                                    <span className="truncate text-sidebar-foreground text-[14px]">
                                        {lastQuestion ?? "No question"}
                                    </span>
                                </div>
                                <div
                                    className="flex flex-col"

                                >
                                    <span
                                        className={`text-sm font-medium text-gray-600 ${hoveredConversations[item.ConversationId] ? "opacity-0 pointer-events-none absolute" : "block"
                                            }`}
                                    >
                                        {CustomtDateFormat(item.LastUpdatedDate, { dateDelimiter: "." })}
                                    </span>

                                    <div className={hoveredConversations[item.ConversationId] ? "block" : "opacity-0 absolute"} onClick={(e) => e.stopPropagation()}>
                                        <ConversationActions
                                            conversationId={item.ConversationId}
                                            conversationTitle={item.ConversationTitle}
                                            projectId={projectId}
                                            projectName={project?.name}
                                            invalidateProject={true}
                                            onOpenChange={(open) => setIsDropdownOpen(open)}
                                        />
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>


            <AddFilesModal projectName={project?.name ?? ''} isOpen={openFilesModal} setIsOpen={setOpenFilesModal} projectId={projectId ?? ''} />
            <AddInstructionModal isFetchingProject={isFetchingProject} fetchedProject={fetchedProject} isOpen={isAddInstructionModalOpen} setIsOpen={setIsAddInstructionModalOpen} projectId={projectId ?? ''} refetchProject={refetchProject} />
        </div>
    )
}